// Product search modal — opens on F2 or when barcode matches multiple variants.
import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, THead, TBody, Tr, Th, Td } from "@/components/ui/table";
import { X, Package } from "lucide-react";

interface VariantResult {
  id: string;
  sku: string;
  barcode?: string | null;
  productId: string;
  productName: string;
  sellPrice: string;
  costPrice: string;
  imageUrls?: string[];
}

interface Props {
  open: boolean;
  initialQuery?: string;
  onSelect: (variantId: string) => void;
  onClose: () => void;
}

function fmtVND(s: string) {
  const n = parseFloat(s);
  if (isNaN(n)) return "0";
  return new Intl.NumberFormat("vi-VN").format(n) + " ₫";
}

export function ProductSearchModal({ open, initialQuery = "", onSelect, onClose }: Props) {
  const [q, setQ] = useState(initialQuery);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setQ(initialQuery);
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open, initialQuery]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (open) window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const { data: results = [], isFetching } = useQuery({
    queryKey: ["variant-search", q],
    queryFn: () => q.trim().length >= 1
      ? apiGet<VariantResult[]>("/v1/variants/search", { q: q.trim(), limit: 20 })
      : Promise.resolve([]),
    enabled: open && q.trim().length >= 1,
    staleTime: 5_000,
  });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-2xl rounded-xl border bg-card shadow-xl">
        <div className="flex items-center gap-3 border-b p-4">
          <Input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Tìm theo tên, SKU, barcode..."
            className="flex-1"
          />
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X size={18} />
          </Button>
        </div>

        <div className="max-h-[60vh] overflow-auto">
          {isFetching && (
            <p className="p-4 text-sm text-muted-foreground">Đang tìm...</p>
          )}
          {!isFetching && results.length === 0 && q.trim() && (
            <p className="p-4 text-sm text-muted-foreground">Không tìm thấy sản phẩm</p>
          )}
          {results.length > 0 && (
            <Table>
              <THead>
                <Tr>
                  <Th className="w-10" />
                  <Th>Sản phẩm</Th>
                  <Th>SKU</Th>
                  <Th className="text-right">Giá bán</Th>
                  <Th className="w-20" />
                </Tr>
              </THead>
              <TBody>
                {results.map((v) => (
                  <Tr
                    key={v.id}
                    className="cursor-pointer"
                    onClick={() => { onSelect(v.id); onClose(); }}
                  >
                    <Td className="w-10">
                      {v.imageUrls?.[0] ? (
                        <img src={v.imageUrls[0]} alt="" className="h-8 w-8 rounded object-cover" />
                      ) : (
                        <div className="h-8 w-8 rounded bg-muted flex items-center justify-center">
                          <Package size={14} className="text-muted-foreground" />
                        </div>
                      )}
                    </Td>
                    <Td className="font-medium">{v.productName}</Td>
                    <Td className="font-mono text-sm">{v.sku}</Td>
                    <Td className="text-right">{fmtVND(v.sellPrice)}</Td>
                    <Td>
                      <Button size="sm" variant="outline">Chọn</Button>
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
}
